import React from "react";
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, ReferenceLine } from "recharts";
import axios from "axios";
import { Brand } from "@/components/Layout";
import { COUPLE_T } from "@/data/couple";
import { computeCouple, coupleSynthesis, dialogueTips } from "@/lib/coupleScoring";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TICK_INK = { fill: "#6E5C53", fontSize: 11 };
const TICK_MUTED = { fill: "#a1907f", fontSize: 9 };
const DOMAIN = [0, 20];
const C1 = "#B58C67";
const C2 = "#6E8B7E";

const SectionTitle = ({ children, sub }) => (
  <div className="text-center mb-6">
    <h3 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)]">{children}</h3>
    {sub && <p className="text-sm text-[var(--ink-soft)] mt-1">{sub}</p>}
  </div>
);

export const CoupleResults = ({ p1, p2, lang, onRestart, persist = false }) => {
  const t = COUPLE_T[lang];
  const n1 = p1.name || t.partner1;
  const n2 = p2.name || t.partner2;
  const results = React.useMemo(() => computeCouple(p1, p2, lang), [p1, p2, lang]);
  const synthesis = React.useMemo(() => coupleSynthesis(results, p1, p2, lang), [results, p1, p2, lang]);
  const tips = React.useMemo(() => dialogueTips(results, lang), [results, lang]);

  React.useEffect(() => {
    if (!persist) return;
    axios.post(`${API}/besoins-couple-evaluations`, { p1, p2, lang }).catch((e) => console.error("save couple failed", e));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const radarData = results.rows.map((r) => ({ subject: r.short, [n1]: r.m1, [n2]: r.m2 }));
  const perceptionData = results.rows.map((r) => ({ name: r.short, [n1]: r.p1Err, [n2]: r.p2Err }));

  return (
    <div className="animate-fade-up print-container" data-testid="couple-results-view">
      <div className="hidden print:block mb-6"><Brand /></div>

      <div className="text-center mb-10">
        <h2 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-4">{t.thanks}</h2>
        <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto">{t.thanksText}</p>
      </div>

      {/* Radar superposé */}
      <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-5 mb-10 print-break">
        <SectionTitle sub={t.radarSub}>{t.radarTitle}</SectionTitle>
        <ResponsiveContainer width="100%" height={380}>
          <RadarChart data={radarData} outerRadius="70%">
            <PolarGrid stroke="#d8ccbe" />
            <PolarAngleAxis dataKey="subject" tick={TICK_INK} />
            <PolarRadiusAxis domain={DOMAIN} tick={TICK_MUTED} angle={90} />
            <Radar name={n1} dataKey={n1} stroke={C1} fill={C1} fillOpacity={0.35} strokeWidth={2} />
            <Radar name={n2} dataKey={n2} stroke={C2} fill={C2} fillOpacity={0.35} strokeWidth={2} />
            <Legend />
            <Tooltip formatter={(v) => [`${v}/20`, ""]} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Tableau besoin par besoin */}
      <div className="mb-10 print-break">
        <SectionTitle>{t.tableTitle}</SectionTitle>
        <div className="overflow-x-auto rounded-2xl border border-[var(--border)]">
          <table className="w-full text-sm" data-testid="couple-recap-table">
            <thead>
              <tr className="bg-[var(--gold)] text-white text-left">
                <th className="p-3 font-medium">{t.colNeed}</th>
                <th className="p-3 font-medium text-right">{n1}</th>
                <th className="p-3 font-medium text-right">{n2}</th>
                <th className="p-3 font-medium text-right">{t.colGap}</th>
                <th className="p-3 font-medium">{t.colStatus}</th>
              </tr>
            </thead>
            <tbody>
              {results.rows.map((r, i) => (
                <tr key={r.key} className={i % 2 ? "bg-white/40" : "bg-white/70"}>
                  <td className="p-3 text-[var(--ink)]">{r.short}</td>
                  <td className="p-3 text-right font-semibold" style={{ color: C1 }}>{r.m1}</td>
                  <td className="p-3 text-right font-semibold" style={{ color: C2 }}>{r.m2}</td>
                  <td className="p-3 text-right text-[var(--ink-soft)]">{r.gap > 0 ? "+" : ""}{r.gap}</td>
                  <td className="p-3">
                    <span className="inline-block rounded-full px-2.5 py-0.5 text-xs text-white" style={{ backgroundColor: r.status.color }}>{r.status.label}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Connaissance mutuelle */}
      <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-5 sm:p-7 mb-10 print-break">
        <SectionTitle sub={t.knowSub}>{t.knowTitle}</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[[`${n1} ${t.knows} ${n2}`, results.knowledge.p1Accuracy], [`${n2} ${t.knows} ${n1}`, results.knowledge.p2Accuracy]].map(([label, acc]) => (
            <div key={label} className="rounded-2xl bg-[var(--sand)] border border-[var(--border)] p-5 text-center">
              <p className="text-sm text-[var(--ink)] mb-2">{label}</p>
              <p className="font-serif-title text-5xl text-[var(--gold)]">{acc}<span className="text-2xl"> %</span></p>
              <p className="text-xs text-[var(--ink-soft)] mt-1">{t.accuracy}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Écart perçu / réel besoin par besoin */}
      <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-5 sm:p-7 mb-10 print-break">
        <SectionTitle sub={t.perceptionSub}>{t.perceptionTitle}</SectionTitle>
        <ResponsiveContainer width="100%" height={430}>
          <BarChart data={perceptionData} layout="vertical" margin={{ left: 20, right: 30 }} barGap={2}>
            <XAxis type="number" domain={[-20, 20]} tick={TICK_INK} />
            <YAxis type="category" dataKey="name" width={110} tick={{ fill: "#4A3B36", fontSize: 12 }} />
            <ReferenceLine x={0} stroke="#a1907f" />
            <Legend />
            <Tooltip formatter={(v) => [`${v > 0 ? "+" : ""}${v}`, ""]} />
            <Bar dataKey={n1} fill={C1} radius={[3, 3, 3, 3]} minPointSize={2} />
            <Bar dataKey={n2} fill={C2} radius={[3, 3, 3, 3]} minPointSize={2} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Accord / Divergence */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-6 print-break">
          <h4 className="font-serif-title text-2xl mb-1" style={{ color: "#8A9A6D" }}>{t.alignedTitle}</h4>
          <p className="text-xs text-[var(--ink-soft)] mb-3">{t.alignedNote}</p>
          <ul className="space-y-1.5 text-[var(--ink)]">
            {results.aligned.map((r) => (<li key={r.key}>• {r.short} <span className="text-[var(--ink-soft)]">({r.m1}/{r.m2})</span></li>))}
            {results.aligned.length === 0 && <li className="text-sm text-[var(--ink-soft)]">{t.none}</li>}
          </ul>
        </div>
        <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-6 print-break">
          <h4 className="font-serif-title text-2xl mb-1" style={{ color: "#B0553B" }}>{t.divergentTitle}</h4>
          <p className="text-xs text-[var(--ink-soft)] mb-3">{t.divergentNote}</p>
          <ul className="space-y-1.5 text-[var(--ink)]">
            {results.divergent.map((r) => (<li key={r.key}>• {r.short} <span className="text-[var(--ink-soft)]">({r.m1}/{r.m2})</span></li>))}
            {results.divergent.length === 0 && <li className="text-sm text-[var(--ink-soft)]">{t.none}</li>}
          </ul>
        </div>
      </div>

      {/* Pistes de dialogue */}
      <div className="rounded-3xl bg-white/60 border border-[var(--border)] p-6 sm:p-8 mb-8 print-break">
        <h3 className="font-serif-title text-3xl text-[var(--gold)] mb-5">{t.dialogueTitle}</h3>
        <ul className="space-y-3">
          {tips.map((d) => (
            <li key={d.key} className="rounded-xl bg-[var(--sand)] border border-[var(--border)] p-4">
              <p className="text-xs uppercase tracking-[0.14em] text-[var(--gold)] mb-1">{d.short}</p>
              <p className="text-sm text-[var(--ink)] leading-relaxed">{d.tip}</p>
            </li>
          ))}
        </ul>
      </div>

      {/* Synthèse */}
      <div className="rounded-3xl bg-[var(--gold)] text-white p-6 sm:p-9 mb-10 print-break">
        <h3 className="font-serif-title text-3xl mb-4">{t.synthTitle}</h3>
        <p className="leading-relaxed text-[0.98rem]" data-testid="couple-synthesis">{synthesis}</p>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-4 no-print">
        <button onClick={() => window.print()} data-testid="couple-btn-export" className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all">{t.exportPdf}</button>
        <button onClick={onRestart} data-testid="couple-btn-restart" className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">{t.restart}</button>
      </div>
    </div>
  );
};
